import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";

import { useDispatch, useSelector } from "react-redux";
import { WebRenderer } from "@wharfkit/web-renderer";
import { BalanceDropdown } from "./BalanceDropdown";

import "./Header.css";

import { AppState } from "../store";
import {
  WalletLogin,
  WalletLogout,
  isSessionKitReady,
  AutoLogin,
} from "../store/user/actions";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const webRenderer = new WebRenderer();

const drawerWidth = 280;

export const Header = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [showBalanceDropdown, setShowBalanceDropdown] = useState(false);
  const [showMobileBalanceDropdown, setShowMobileBalanceDropdown] = useState(false); // separate state for mobile
  const location = useLocation();
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  const userModule = useSelector((state: AppState) => state.userModule);
  const loginInfo = userModule.loginInfo;

  const [account, setAccount] = useState<string>("");

  useEffect(() => {
    const initAuth = async () => {
      setIsAuthLoading(true);
      await dispatch(AutoLogin());
      // Give some time for the authentication to complete
      setTimeout(() => {
        setIsAuthLoading(false);
      }, 1000);
    };
    
    if (isSessionKitReady()) {
      initAuth();
    }
  }, [isSessionKitReady()]);

  async function login() {
    if (account != "") {
      if (window.confirm("Do you want to logout?")) {
        dispatch(WalletLogout());
        navigate("/");
      }
      return;
    }
    dispatch(WalletLogin());
  }

  async function logout() {
    if (account != "") {
      dispatch(WalletLogout());
      navigate("/");
    }
  }

  useEffect(() => {
    setAccount(loginInfo.account.toString());
    if (loginInfo.account.toString() == "" && !loginInfo.isLogin && !isAuthLoading) {
      navigate("/");
    }
  }, [loginInfo.isLogin, loginInfo.account, isAuthLoading]);

  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const checkAdmin = () => {
    if (loginInfo.account.toString() == "f4wqy.wam" || loginInfo.account.toString() == "rwkwg.wam" || loginInfo.account.toString() == "captxak12345") {
      return true;
    }
    return false;
    
  }
  const navItems = [
    { path: "dashboard", label: "DASHBOARD" },
    { path: "explore", label: "EXPLORE" },
    { path: "claim", label: "CLAIM" },
    { path: "inventory", label: "INVENTORY" },
    { path: "tools", label: "TOOLS" },
    { path: "staking", label: "STAKING" },
    { path: "swap", label: "SWAP" },
    { path: "tokens", label: "TOKENS" },
    { path: "leaderboard", label: "LEADERBOARD" },
    { path: "tournament", label: "TOURNAMENT" },
  ];

  if(checkAdmin()) {
    navItems.push({ path: "admin", label: "ADMIN" });
  }

  const drawer = (
    <Box sx={{ 
      textAlign: "center", 
      pt: 2, 
      px: 3,
      height: "100%",
      backgroundImage: "linear-gradient(135deg, #3a2609 0%, #5c3c10 100%)",
      position: "relative",
      overflow: "hidden"
    }}>
      {/* Wood texture overlay */}
      <Box sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "linear-gradient(45deg, rgba(92, 60, 16, 0.2) 25%, transparent 25%, transparent 50%, rgba(92, 60, 16, 0.2) 50%, rgba(92, 60, 16, 0.2) 75%, transparent 75%, transparent)",
        backgroundSize: "10px 10px",
        opacity: 0.3,
        zIndex: 0,
      }} />
      {/* Scrollable content container for mobile navigation */}
      <Box sx={{
        position: "relative",
        zIndex: 1,
        overflowY: "auto",
        maxHeight: "100vh",
        pb: 2,
        scrollBehavior: "smooth",
        // Add custom scrollbar styling for better engagement
        '&::-webkit-scrollbar': {
          width: '8px',
          background: 'rgba(92, 60, 16, 0.15)'
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(135deg, #f5e1a3 0%, #7d5014 100%)',
          borderRadius: '8px',
        },
        '&:hover::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(135deg, #ffe9a3 0%, #a97d3a 100%)',
        },
        transition: 'box-shadow 0.3s',
        boxShadow: '0 2px 16px 0 rgba(245, 225, 163, 0.08)',
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src="/images/logos.png"
              alt="WAX Deer Logo"
              style={{
                width: "120px",
                height: "auto",
              }}
            />
          </Box>
          <IconButton 
            onClick={handleDrawerToggle}
            sx={{ 
              color: "#f5e1a3",
              "&:hover": {
                backgroundColor: "rgba(245, 225, 163, 0.1)",
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          mb: 4, 
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          p: 2,
          borderRadius: "12px",
          border: "1px solid rgba(245, 225, 163, 0.3)"
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              color: "#f5e1a3",
              fontWeight: "bold",
              fontSize: "18px",
              textAlign: "center",
              textOverflow: "ellipsis",
              overflow: "hidden"
            }}
          >
            {account ? account : "Not Connected"}
          </Typography>
          
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={account ? <LogoutIcon /> : <LoginIcon />}
              onClick={login}
              sx={{
                color: "#f5e1a3",
                borderColor: "#f5e1a3",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(245, 225, 163, 0.1)",
                },
              }}
            >
              {account ? "Logout" : "Login"}
            </Button>
            {/* {account && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={() => navigate('/balance')}
                sx={{
                  color: "#f5e1a3",
                  borderColor: "#f5e1a3",
                  "&:hover": {
                    borderColor: "#fff",
                    backgroundColor: "rgba(245, 225, 163, 0.1)",
                  },
                }}
              >
                Balances
              </Button>
            )} */}
          </Box>
        </Box>
        
        <Divider sx={{ borderColor: "rgba(245, 225, 163, 0.3)", mb: 2 }} />
        
        {account && (
          <List sx={{ 
            p: 1, 
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            borderRadius: "12px",
            border: "1px solid rgba(245, 225, 163, 0.2)"
          }}>
            {navItems.map((item, index) => (
              <React.Fragment key={item.path}>
                <Link 
                  to={"/" + item.path}
                  style={{ textDecoration: "none" }}
                  onClick={handleDrawerToggle} // Auto-close drawer on link click
                >
                  <ListItem disablePadding>
                    <ListItemButton 
                      sx={{ 
                        textAlign: "center",
                        borderRadius: "8px",
                        mb: 0.5,
                        backgroundColor: location.pathname.indexOf(item.path) === 1 
                          ? "rgba(245, 225, 163, 0.15)" 
                          : "transparent",
                        "&:hover": {
                          backgroundColor: "rgba(245, 225, 163, 0.1)",
                        }
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        sx={{
                          color: location.pathname.indexOf(item.path) === 1
                            ? "#f5e1a3"
                            : "rgba(255, 255, 255, 0.8)",
                          "& .MuiTypography-root": {
                            fontWeight: location.pathname.indexOf(item.path) === 1 ? "bold" : "normal",
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </Link>
                {index < navItems.length - 1 && (
                  <Divider sx={{ borderColor: "rgba(245, 225, 163, 0.1)", my: 0.5 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  const container =
    window !== undefined ? () => window.document.body : undefined;

  return (
    <Box
      sx={{
        flexGrow: 1,
        maxWidth: "1400px",
        position: "relative",
        margin: "auto",
        zIndex: 100,
      }}
    >
      <AppBar
        position="static"
        sx={{ 
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.25)", 
          borderRadius: { xs: 0, md: "0 0 16px 16px" },
          backgroundImage: "linear-gradient(135deg, #3a2609 0%, #5c3c10 100%)",
          border: "none",
          borderBottom: "4px solid #7d5014",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Wood texture overlay */}
        <Box sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(45deg, rgba(92, 60, 16, 0.2) 25%, transparent 25%, transparent 50%, rgba(92, 60, 16, 0.2) 50%, rgba(92, 60, 16, 0.2) 75%, transparent 75%, transparent)",
          backgroundSize: "10px 10px",
          opacity: 0.3,
          zIndex: 0,
        }} />
        
        <Toolbar sx={{ 
          paddingTop: { xs: "10px", sm: "10px", md: "12px" },
          paddingBottom: { xs: "10px", sm: "10px", md: "12px" },
          position: "relative", 
          zIndex: 1 
        }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: { xs: 0, md: 4 },
            }}
          >
            <img
              src="/images/logos.png"
              alt="WAX Deer Logo"
              style={{
                height: "40px",
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
          
          {account && (
            <Box
              sx={{ 
                display: { xs: "none", md: "flex" },
                flexGrow: 1,
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                borderRadius: "8px",
                padding: "4px",
                border: "1px solid rgba(245, 225, 163, 0.2)",
                justifyContent: "center"
              }}
            >
              {navItems.map((item, index) => (
                <Link 
                  key={item.path} 
                  to={"/" + item.path}
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    sx={{
                      color: location.pathname.indexOf(item.path) === 1
                        ? "#f5e1a3"
                        : "rgba(255, 255, 255, 0.8)",
                      fontWeight: location.pathname.indexOf(item.path) === 1 ? "bold" : "normal",
                      "&:hover": {
                        color: "#f5e1a3",
                        backgroundColor: "rgba(245, 225, 163, 0.05)",
                      },
                      borderRight: index + 1 !== navItems.length ? "1px solid rgba(245, 225, 163, 0.2)" : "",
                      borderRadius: 0,
                      px: 1.5,
                      py: 1,
                      minWidth: "auto",
                      fontSize: "13px"
                    }}
                  >
                    {item.label}
                  </Button>
                </Link>
              ))}
            </Box>
          )}
          
          <Box sx={{ 
            display: "flex",
            ml: "auto", 
            alignItems: "center", 
            gap: 1 
          }}>
            {account && (
              <Button
                variant="outlined"
                sx={{ 
                  display: { xs: "none", md: "flex" },
                  color: "#f5e1a3",
                  borderColor: "#f5e1a3",
                  "&:hover": {
                    borderColor: "#fff",
                    backgroundColor: "rgba(245, 225, 163, 0.1)",
                  },
                  mr: 1
                }}
                onClick={() => navigate('/balance')}
                startIcon={<AccountBalanceWalletIcon />}
              >
                Balances
              </Button>
            )}
            
            <Button
              sx={{ 
                display: { xs: "none", md: "flex" },
                backgroundColor: account ? "rgba(220, 53, 69, 0.8)" : "#4a5af8",
                color: "white",
                "&:hover": {
                  backgroundColor: account ? "rgba(200, 35, 51, 0.9)" : "#3949db",
                },
                borderRadius: "8px"
              }}
              variant="contained"
              onClick={login}
              startIcon={account ? <LogoutIcon /> : <LoginIcon />}
            >
              {account ? (
                <Typography
                  sx={{
                    maxWidth: "120px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {account}
                </Typography>
              ) : (
                "Login"
              )}
            </Button>
            
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ 
                display: { md: "none" },
                color: "#f5e1a3"
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          
          {showBalanceDropdown && (
            <Box 
              sx={{ 
                position: "absolute", 
                top: "100%", 
                right: 16,
                zIndex: 10,
                display: { xs: "none", md: "block" }
              }}
            >
              <BalanceDropdown onClose={() => setShowBalanceDropdown(false)} />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          anchor="right"
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: { xs: "100%", sm: drawerWidth },
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
};
